import { useEffect, useRef, useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import type { MessagingConversation } from '../../../api/generated/model/messagingConversation'
import type { MessagingMessage } from '../../../api/generated/model/messagingMessage'
import type { MessagingMessageCreateKind } from '../../../api/generated/model/messagingMessageCreateKind'
import { useMessagingAttachmentSource } from '../../../api/attachments'
import {
  getGetMessagingUnreadSummaryQueryKey,
  getListMessagingConversationsQueryKey,
  getListMessagingMessagesQueryKey,
  useCreateMessagingMessage,
  useListMessagingMessages,
  useMarkMessagingConversationRead,
} from '../../../api/generated/messaging/messaging'
import { useT } from '../../../i18n'
import { messagingI18n } from '../../../i18n/messaging'
import { useMessagingUiStore } from '../../../store/messaging-ui'
import { EntityConversationPanel } from './EntityConversationPanel'
import {
  MessageBubble,
  type MessageDeliveryState,
  type MessagingSurface,
} from './MessageBubble'
import { MessageComposer } from './MessageComposer'
import { MessageAttachments } from './MessageAttachments'
import { validateMessageProjection } from './entity-safety'

type ConversationThreadProps = {
  conversation: MessagingConversation
  surface: MessagingSurface
  destination: string
  canSend: boolean
  canUseInternalNotes: boolean
  embedded?: boolean
  layout?: 'standalone' | 'compact' | 'expanded'
  focusComposerOnMount?: boolean
  onComposerFocusConsumed?: () => void
}

type PendingMessage = {
  message: MessagingMessage
  deliveryState: Exclude<MessageDeliveryState, 'sent'>
}

type SendInput = {
  body: string
  kind: MessagingMessageCreateKind
  attachment_ids: string[]
}

function clientMessageId() {
  if (typeof globalThis.crypto?.randomUUID === 'function') {
    return globalThis.crypto.randomUUID()
  }
  return `msg-${Date.now()}-${Math.random().toString(16).slice(2)}`
}

export function ConversationThread({
  conversation,
  surface,
  destination,
  canSend,
  canUseInternalNotes,
  embedded = false,
  layout = 'standalone',
  focusComposerOnMount = false,
  onComposerFocusConsumed,
}: ConversationThreadProps) {
  const t = useT(messagingI18n)
  const messenger = layout !== 'standalone'
  const queryClient = useQueryClient()
  const [pending, setPending] = useState<PendingMessage[]>([])
  const timelineRef = useRef<HTMLDivElement>(null)
  const attachmentSource = useMessagingAttachmentSource(conversation.id)
  const messagesQuery = useListMessagingMessages(
    conversation.id,
    { limit: 100 },
    {
      query: {
        retry: false,
        refetchOnWindowFocus: false,
      },
    },
  )
  const createMessage = useCreateMessagingMessage()
  const markRead = useMarkMessagingConversationRead()
  const markedThroughRef = useRef<string | null>(null)
  const rawMessages =
    messagesQuery.data?.status === 200 ? messagesQuery.data.data.items : []
  const messageResults = rawMessages.map((message) =>
    validateMessageProjection(message, conversation.id),
  )
  const hasBlockedMessage = messageResults.some((result) => !result.safe)
  const messages = hasBlockedMessage
    ? []
    : messageResults
        .flatMap((result) => (result.safe ? [result.value] : []))
        .reverse()
  const isGeneralSupport =
    conversation.topic === 'general' &&
    conversation.entity_link.entity_type === 'organization'
  const threadUnavailable = messagesQuery.isError || hasBlockedMessage
  const latestMessageId = rawMessages[0]?.id ?? null

  useEffect(() => {
    if (
      surface === 'admin' ||
      conversation.unread_count < 1 ||
      latestMessageId === null ||
      markedThroughRef.current === latestMessageId
    ) {
      return
    }
    const lastMessageId = latestMessageId
    markedThroughRef.current = lastMessageId
    void markRead
      .mutateAsync({
        conversationId: conversation.id,
        data: { last_read_message_id: lastMessageId },
      })
      .then(() =>
        Promise.all([
          queryClient.invalidateQueries({
            queryKey: getGetMessagingUnreadSummaryQueryKey(),
          }),
          queryClient.invalidateQueries({
            queryKey: getListMessagingConversationsQueryKey(),
          }),
        ]),
      )
      .catch(() => {
        markedThroughRef.current = null
      })
  }, [
    conversation.id,
    conversation.unread_count,
    markRead,
    queryClient,
    latestMessageId,
    surface,
  ])

  useEffect(() => {
    if (focusComposerOnMount) onComposerFocusConsumed?.()
  }, [focusComposerOnMount, onComposerFocusConsumed])

  useEffect(() => {
    if (!messenger || threadUnavailable) return
    const timeline = timelineRef.current
    if (timeline) timeline.scrollTop = timeline.scrollHeight
  }, [messenger, messages.length, pending.length, threadUnavailable])

  const send = async (
    input: SendInput,
    existingClientMessageId?: string,
  ) => {
    const idempotencyKey = existingClientMessageId ?? clientMessageId()
    const optimistic: MessagingMessage = {
      id: `optimistic-${idempotencyKey}`,
      conversation_id: conversation.id,
      client_message_id: idempotencyKey,
      kind: input.kind,
      body: input.body,
      author_kind: surface === 'admin' ? 'pexify_user' : 'app_user',
      attachment_ids: input.attachment_ids,
      reply_to_message_id: null,
      created_at: new Date().toISOString(),
      edited_at: null,
      retracted_at: null,
    }

    setPending((current) => {
      const rest = current.filter(
        (item) => item.message.client_message_id !== idempotencyKey,
      )
      return [...rest, { message: optimistic, deliveryState: 'pending' }]
    })

    try {
      const result = await createMessage.mutateAsync({
        conversationId: conversation.id,
        data: {
          client_message_id: idempotencyKey,
          kind: input.kind,
          body: input.body,
          attachment_ids: input.attachment_ids,
          reply_to_message_id: null,
        },
      })
      if (result.status !== 201) throw new Error('Messaging send failed')

      setPending((current) =>
        current.filter(
          (item) => item.message.client_message_id !== idempotencyKey,
        ),
      )
      await queryClient.invalidateQueries({
        queryKey: getListMessagingMessagesQueryKey(conversation.id, {
          limit: 100,
        }),
      })
    } catch (error) {
      setPending((current) =>
        current.map((item) =>
          item.message.client_message_id === idempotencyKey
            ? { ...item, deliveryState: 'failed' }
            : item,
        ),
      )
      throw error
    }
  }

  const retry = async (item: PendingMessage) => {
    try {
      await send(
        {
          body: item.message.body,
          kind: item.message.kind as MessagingMessageCreateKind,
          attachment_ids: item.message.attachment_ids ?? [],
        },
        item.message.client_message_id ?? undefined,
      )
      const store = useMessagingUiStore.getState()
      if (store.getDraft(surface, conversation.id).trim() === item.message.body) {
        store.clearDraft(surface, conversation.id)
      }
    } catch {
      // The failed bubble remains retryable; send already recorded its state.
    }
  }

  return (
    <section
      data-testid="conversation-thread"
      data-layout={layout}
      className={
        messenger
          ? 'flex min-h-0 flex-1 flex-col overflow-hidden bg-[#12161d] text-white'
          : embedded
          ? 'flex min-h-0 flex-1 flex-col overflow-hidden bg-[var(--color-surface)]'
          : 'flex min-h-[34rem] flex-col overflow-hidden rounded-xl border border-[var(--color-border)] bg-[var(--color-surface)]'
      }
    >
      {!messenger ? <header className="flex items-start justify-between gap-4 border-b border-[var(--color-border)] px-4 py-3">
        <div className="min-w-0">
          <h2 className="truncate text-sm font-semibold text-[var(--color-text)]">
            {conversation.subject || conversation.entity_link.label}
          </h2>
          <p className="mt-0.5 text-xs capitalize text-[var(--color-text-muted)]">
            {conversation.status}
          </p>
        </div>
        <button
          type="button"
          className="rounded-md border border-[var(--color-border)] bg-[var(--color-surface)] px-2.5 py-1.5 text-xs font-semibold text-[var(--color-text)] hover:bg-[var(--color-surface-muted)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500"
          onClick={() => void messagesQuery.refetch()}
        >
          {t.conversation.refresh}
        </button>
      </header> : null}

      {!isGeneralSupport || !messenger ? (
        <div className={layout === 'compact' ? '' : layout === 'expanded' ? 'border-b border-white/10 p-3' : 'border-b border-[var(--color-border)] p-3'}>
          <EntityConversationPanel
            conversation={conversation}
            surface={surface}
            variant={layout === 'standalone' ? 'default' : layout}
          />
        </div>
      ) : null}

      <div
        ref={timelineRef}
        className={
          messenger
            ? 'flex-1 space-y-3 overflow-y-auto bg-[#12161d] px-4 py-5'
            : 'flex-1 space-y-3 overflow-y-auto bg-[var(--color-surface-muted)] p-4'
        }
        aria-live="polite"
      >
        {messagesQuery.isLoading ? (
          <p className={messenger ? 'text-sm text-white/60' : 'text-sm text-[var(--color-text-muted)]'} role="status">
            {t.states.loading}
          </p>
        ) : null}
        {messagesQuery.isError ? (
          <div className={messenger ? 'rounded-xl border border-rose-400/30 bg-rose-400/10 p-3 text-sm text-rose-100' : 'rounded-xl border border-[var(--color-danger)] bg-[color-mix(in_srgb,var(--color-danger)_10%,var(--color-surface))] p-3 text-sm text-[var(--color-danger)]'} role="alert">
            <p>{t.conversation.messageHistoryUnavailable}</p>
            <button
              type="button"
              className={messenger ? 'mt-2 rounded-lg border border-rose-200/30 px-3 py-1.5 text-xs font-semibold hover:bg-white/10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/70' : 'mt-2 rounded-lg border border-[var(--color-danger)] px-3 py-1.5 text-xs font-semibold hover:bg-[var(--color-surface-muted)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-danger)]'}
              onClick={() => void messagesQuery.refetch()}
            >
              {t.conversation.retryMessages}
            </button>
          </div>
        ) : null}
        {hasBlockedMessage ? (
          <p
            className="rounded-lg border border-[var(--color-warning)] bg-[color-mix(in_srgb,var(--color-warning)_10%,var(--color-surface))] px-3 py-2 text-sm text-[var(--color-warning-foreground)]"
            role="alert"
          >
            {t.states.projectionUnavailable}
          </p>
        ) : null}
        {!messagesQuery.isLoading &&
        !messagesQuery.isError &&
        !hasBlockedMessage &&
        messages.length === 0 &&
        pending.length === 0 ? (
          isGeneralSupport && messenger ? (
            <div className="max-w-[88%] rounded-[1.25rem] rounded-bl-md bg-[#272b33] px-4 py-3 text-sm text-white shadow-sm">
              <p className="font-semibold">{t.conversation.supportWelcomeTitle}</p>
              <p className="mt-1 leading-5 text-white/90">
                {t.conversation.supportWelcomeBody}
              </p>
              <p className="mt-2 text-xs text-white/55">PEXIFY</p>
            </div>
          ) : (
            <p className={messenger ? 'text-center text-sm text-white/55' : 'text-center text-sm text-[var(--color-text-muted)]'}>
              {t.conversation.noConversations}
            </p>
          )
        ) : null}
        {!hasBlockedMessage ? messages.map((message) => (
          <div key={message.id}>
            <MessageBubble
              message={message}
              surface={surface}
              counterpartLabel={destination}
              appearance={messenger ? 'messenger' : 'default'}
            />
            {!message.retracted_at ? (
              <div
                className={
                  (surface === 'admin'
                    ? message.author_kind === 'pexify_user'
                    : message.author_kind === 'app_user')
                    ? 'ml-auto max-w-[min(34rem,88%)]'
                    : 'max-w-[min(34rem,88%)]'
                }
              >
                <MessageAttachments
                  attachmentIds={message.attachment_ids ?? []}
                  rowsById={attachmentSource.rowsById}
                  loading={attachmentSource.isLoading}
                  unavailable={attachmentSource.isError}
                  appearance={messenger ? 'messenger' : 'default'}
                />
              </div>
            ) : null}
          </div>
        )) : null}
        {pending.map((item) => (
          <div key={item.message.id}>
            <MessageBubble
              message={item.message}
              surface={surface}
              counterpartLabel={destination}
              deliveryState={item.deliveryState}
              onRetry={
                item.deliveryState === 'failed'
                  ? () => void retry(item)
                  : undefined
              }
              appearance={messenger ? 'messenger' : 'default'}
            />
            {!item.message.retracted_at ? (
              <div className="ml-auto max-w-[min(34rem,88%)]">
                <MessageAttachments
                  attachmentIds={item.message.attachment_ids ?? []}
                  rowsById={attachmentSource.rowsById}
                  loading={attachmentSource.isLoading}
                  unavailable={attachmentSource.isError}
                  appearance={messenger ? 'messenger' : 'default'}
                />
              </div>
            ) : null}
          </div>
        ))}
      </div>

      <MessageComposer
        autoFocus={focusComposerOnMount}
        surface={surface}
        conversationId={conversation.id}
        destination={destination}
        canSend={canSend && !threadUnavailable}
        canUseInternalNotes={canUseInternalNotes}
        attachmentSource={attachmentSource}
        onSend={send}
        appearance={messenger ? 'messenger' : 'default'}
        disabledReason={
          threadUnavailable || !canSend ? t.composer.readOnly : undefined
        }
      />
    </section>
  )
}
